import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

const PLUM = "#3E1F46", EDGE = "#E8DFCB";
const btn = "inline-flex items-center gap-1.5 h-9 px-3 rounded text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-40";

/** Operational holds: active structured holds + legacy note-based holds awaiting a human decision. Nothing here changes money. */
export default function AdminHolds() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/order-holds").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load holds."));
  useEffect(() => { load(); }, []);

  const act = async (o, path, body, ok) => {
    setBusy(o.id);
    try { await api.post(`/admin/orders/${o.id}/hold${path}`, body); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't save."); } finally { setBusy(""); }
  };
  const ref = (o) => o.order_number ? `#${o.order_number}` : `#${String(o.id || o.order_id || "").slice(0, 8)}`;

  if (!data) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading holds…</div>;
  return (
    <div data-testid="admin-holds">
      <header className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Heritage Dahlias · Orders & Packing</div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">Holds to review</h1>
        <p className="text-[#7A6A55] mt-2 max-w-3xl text-sm">{data.scope}</p>
      </header>

      <section className="bg-[#FFF8E6] border-2 border-[#C49247] rounded-md p-4 mb-6" data-testid="legacy-holds">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#A33030]"><ShieldAlert className="w-4 h-4" /> Legacy hold notes · {data.note_based_for_review.length} to decide</div>
        {data.note_based_for_review.length === 0 ? <p className="text-sm text-[#7A6A55] mt-2" data-testid="legacy-holds-empty">No unreviewed hold wording in any open order.</p> : (
          <ul className="mt-3 divide-y divide-[#C49247]/30">
            {data.note_based_for_review.map((o) => (
              <li key={o.id} className="py-3 grid md:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`legacy-hold-${o.id}`}>
                <div>
                  <Link to={`/admin/order/${o.id}`} className="font-serif text-[15px] text-[#3C2A1E] underline">{o.customer_name || "Unknown"} · {ref(o)}</Link>
                  <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-[#9C8B73]">{String(o.stage || "").replace(/_/g, " ")} · {o.payment_status || "unpaid"}</span>
                  <div className="text-sm italic text-[#3C2A1E] mt-1">“{o.matched_note}”</div>
                </div>
                <div className="flex gap-2">
                  <button type="button" disabled={busy === o.id} onClick={() => act(o, "", { reason: o.matched_note.slice(0, 120), kind: /refund/i.test(o.matched_note) ? "refund_reconciliation" : "other", blocking: true }, "Structured hold placed.")} data-testid={`legacy-place-${o.id}`} className={btn + " bg-[#A33030] text-white"}>Place hold</button>
                  <button type="button" disabled={busy === o.id} onClick={() => act(o, "/clear-note", { decision: "cleared", note: "Reviewed from Holds list" }, "Cleared , not a hold.")} data-testid={`legacy-clear-${o.id}`} className={btn + " bg-white border"} style={{ borderColor: EDGE, color: "#3C2A1E" }}>Clear</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid="active-holds">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>Active structured holds · {data.active.length}</div>
        {data.active.length === 0 ? <p className="text-sm text-[#7A6A55] mt-2">None.</p> : (
          <ul className="mt-3 divide-y" style={{ borderColor: EDGE }}>
            {data.active.map((h) => (
              <li key={h.id} className="py-3 text-sm" data-testid={`active-hold-${h.order_id}`}>
                <Link to={`/admin/order/${h.order_id}`} className="font-serif underline text-[#3C2A1E]">{h.customer_name || "Order"} · {h.order_number ? `#${h.order_number}` : `#${h.order_id.slice(0, 8)}`}</Link>
                <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-[#9C8B73]">{String(h.kind).replace(/_/g, " ")} · since {String(h.created_at).slice(0, 10)}{h.owner ? ` · ${h.owner}` : ""}</span>
                <div className="text-[#3C2A1E] mt-0.5">{h.reason}</div>
                <div className="text-[11px] text-[#7A6A55]">Resolve from the order page (a resolution note is recorded).</div>
              </li>
            ))}
          </ul>
        )}
      </section>
      {data.cleared_notes?.length > 0 && (
        <p className="text-[11px] text-[#7A6A55] mt-4 inline-flex items-center gap-1" data-testid="cleared-notes"><CheckCircle2 className="w-3.5 h-3.5" /> {data.cleared_notes.length} note{data.cleared_notes.length === 1 ? "" : "s"} reviewed and cleared , the wording stays on the order, only the decision is recorded.</p>
      )}
    </div>
  );
}
